import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useMemo, useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { GripVertical, Filter, ZoomIn, ZoomOut, RotateCcw, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { toast } from "sonner";
import { parseLocalDate } from "@/lib/dates";

interface Transaction {
  id: string;
  type: "income" | "expense";
  amount: number;
  category: string;
  account?: string | null;      // legacy text field, may be absent
  account_id?: string | null;   // current UUID field
  description: string | null;
  date: string;
}

interface Account {
  id: string;
  name: string;
  parent_account_id: string | null;
  initial_balance: number;
  balance_as_of_date?: string | null;
}

interface SankeyDiagramProps {
  transactions: Transaction[];
}

interface FlowNode {
  id: string;
  label: string;
  value: number;
  x: number;
  y: number;
  height: number;
  color: string;
  level: number;
  nodeType?: "total" | "income-source" | "account" | "expense-group" | "category";
}

interface FlowLink {
  source: FlowNode;
  target: FlowNode;
  value: number;
  color?: string;
}

interface DragState {
  nodeId: string | null;
  startX: number;
  startY: number;
  offsetX: number;
  offsetY: number;
}

interface CategoryClassification {
  category: string;
  group: string;
}

// Color palette for expense groups
const GROUP_COLORS: Record<string, string> = {
  "Tax": "hsl(20, 80%, 55%)",
  "Savings": "hsl(210, 60%, 60%)",
  "Living": "hsl(145, 50%, 45%)",
  "Transportation": "hsl(280, 50%, 55%)",
  "Healthcare": "hsl(170, 50%, 45%)",
  "Entertainment": "hsl(330, 60%, 55%)",
  "Personal": "hsl(45, 70%, 50%)",
  "Education": "hsl(200, 70%, 50%)",
  "Debt": "hsl(0, 60%, 50%)",
  "Other": "hsl(220, 10%, 50%)",
};

const getGroupColor = (group: string): string => {
  return GROUP_COLORS[group] || GROUP_COLORS["Other"];
};

export const SankeyDiagram = ({ transactions }: SankeyDiagramProps) => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [nodes, setNodes] = useState<FlowNode[]>([]);
  const [links, setLinks] = useState<FlowLink[]>([]);
  const [categoryClassifications, setCategoryClassifications] = useState<Map<string, string>>(new Map());
  const [isClassifying, setIsClassifying] = useState(false);
  const [dragState, setDragState] = useState<DragState>({
    nodeId: null,
    startX: 0,
    startY: 0,
    offsetX: 0,
    offsetY: 0,
  });
  const [timePeriod, setTimePeriod] = useState<string>("all");
  const [selectedAccount, setSelectedAccount] = useState<string>("all");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [zoom, setZoom] = useState(1);
  const svgRef = useRef<SVGSVGElement>(null);
  const { formatCurrency: formatCurrencyPrefs } = useUserPreferences();

  useEffect(() => {
    const fetchAccounts = async () => {
      const { data } = await supabase.from("accounts").select("*");
      if (data) setAccounts(data);
    };
    fetchAccounts();
  }, []);

  // Classify categories using AI
  useEffect(() => {
    const classifyCategories = async () => {
      const expenseCategories = [...new Set(
        transactions.filter(t => t.type === "expense").map(t => t.category)
      )];
      
      if (expenseCategories.length === 0) return;
      
      // Check if we already have classifications for all categories
      const needsClassification = expenseCategories.some(cat => !categoryClassifications.has(cat));
      if (!needsClassification) return;

      setIsClassifying(true);
      try {
        const { data, error } = await supabase.functions.invoke("classify-categories", {
          body: { categories: expenseCategories, type: "expense" }
        });

        if (error) {
          console.error("Classification error:", error);
          // Fallback: assign all to "Other"
          const fallbackMap = new Map<string, string>();
          expenseCategories.forEach(cat => fallbackMap.set(cat, "Other"));
          setCategoryClassifications(fallbackMap);
        } else if (data?.classifications) {
          const newMap = new Map<string, string>();
          data.classifications.forEach((c: CategoryClassification) => {
            newMap.set(c.category, c.group);
          });
          // Ensure all categories have a group
          expenseCategories.forEach(cat => {
            if (!newMap.has(cat)) newMap.set(cat, "Other");
          });
          setCategoryClassifications(newMap);
        }
      } catch (err) {
        console.error("Failed to classify categories:", err);
        toast.error("Failed to classify categories");
      } finally {
        setIsClassifying(false);
      }
    };

    classifyCategories();
  }, [transactions]);

  // Get unique categories and accounts
  const uniqueCategories = useMemo(() => {
    const cats = new Set<string>();
    transactions.forEach(t => cats.add(t.category));
    return Array.from(cats).sort();
  }, [transactions]);

  const uniqueAccounts = useMemo(() => {
    const accs = new Set<string>();
    transactions.forEach(t => {
      const name = t.account || accounts.find(a => a.id === t.account_id)?.name;
      if (name) accs.add(name);
    });
    return Array.from(accs).sort();
  }, [transactions, accounts]);

  // Filter transactions based on selected filters
  const filteredTransactions = useMemo(() => {
    return transactions.filter(t => {
      // Time period filter
      if (timePeriod !== "all") {
        const transactionDate = parseLocalDate(t.date);
        const now = new Date();
        const daysAgo = parseInt(timePeriod);
        const cutoffDate = new Date(now.setDate(now.getDate() - daysAgo));
        if (transactionDate < cutoffDate) return false;
      }
      
      // Account filter
      if (selectedAccount !== "all") {
        const tAccount = t.account || accounts.find(a => a.id === t.account_id)?.name;
        if (tAccount !== selectedAccount) return false;
      }

      // Category filter
      if (selectedCategory !== "all" && t.category !== selectedCategory) return false;

      return true;
    });
  }, [transactions, timePeriod, selectedAccount, selectedCategory, accounts]);

  const initialLayout = useMemo(() => {
    if (accounts.length === 0 || filteredTransactions.length === 0) {
      return { nodes: [], links: [] };
    }

    // Wait for classifications
    const hasExpenses = filteredTransactions.some(t => t.type === "expense");
    if (hasExpenses && categoryClassifications.size === 0) {
      return { nodes: [], links: [] };
    }

    const nodeHeight = 40;
    const minNodeGap = 15;
    const levelWidth = 200;
    
    const allNodes: FlowNode[] = [];
    const flowLinks: FlowLink[] = [];
    const nodesByLevel = new Map<number, FlowNode[]>();

    // Calculate totals
    const totalIncome = filteredTransactions
      .filter(t => t.type === "income")
      .reduce((sum, t) => sum + Number(t.amount), 0);
    
    const totalExpenses = filteredTransactions
      .filter(t => t.type === "expense")
      .reduce((sum, t) => sum + Number(t.amount), 0);

    // Income by category (income sources)
    const incomeByCategory = new Map<string, number>();
    filteredTransactions.filter(t => t.type === "income").forEach(t => {
      incomeByCategory.set(t.category, (incomeByCategory.get(t.category) || 0) + Number(t.amount));
    });

    // Expenses by category
    const expensesByCategory = new Map<string, number>();
    filteredTransactions.filter(t => t.type === "expense").forEach(t => {
      expensesByCategory.set(t.category, (expensesByCategory.get(t.category) || 0) + Number(t.amount));
    });

    // Group expenses by classification
    const expensesByGroup = new Map<string, Map<string, number>>();
    expensesByCategory.forEach((amount, category) => {
      const group = categoryClassifications.get(category) || "Other";
      if (!expensesByGroup.has(group)) {
        expensesByGroup.set(group, new Map());
      }
      expensesByGroup.get(group)!.set(category, amount);
    });

    // Calculate group totals
    const groupTotals = new Map<string, number>();
    expensesByGroup.forEach((categories, group) => {
      const total = Array.from(categories.values()).reduce((sum, v) => sum + v, 0);
      groupTotals.set(group, total);
    });

    // Find accounts used in transactions and calculate their contributions
    const accountNameToAccount = new Map<string, Account>();
    accounts.forEach(acc => accountNameToAccount.set(acc.name, acc));

    // Calculate net flow per account (income - expenses for that account)
    const accountNetFlow = new Map<string, number>(); // positive = income > expenses, negative = expenses > income
    const accountsUsed = new Set<string>();
    
    filteredTransactions.forEach(t => {
      // Extract account name (handle wallet format "Account → Wallet")
      // For new transactions, resolve from account_id; for legacy, use account text
      const rawAccount = t.account || accounts.find(a => a.id === t.account_id)?.name || "Unknown";
      const accountName = rawAccount.includes(" → ") ? rawAccount.split(" → ")[0] : rawAccount;
      accountsUsed.add(accountName);
      
      const current = accountNetFlow.get(accountName) || 0;
      const amount = t.type === "income" ? Number(t.amount) : -Number(t.amount);
      accountNetFlow.set(accountName, current + amount);
    });

    // For accounts where expenses > income, the difference came from existing balance
    const accountStartingBalanceUsed = new Map<string, number>();
    let totalFromAccounts = 0;
    
    accountNetFlow.forEach((netFlow, accountName) => {
      if (netFlow < 0) {
        // This account spent more than it received - difference came from starting balance
        const usedFromBalance = Math.abs(netFlow);
        const account = accountNameToAccount.get(accountName);
        if (account) {
          // Cap at the initial balance (can't use more than was there)
          const actualUsed = Math.min(usedFromBalance, Math.max(0, account.initial_balance));
          if (actualUsed > 0) {
            accountStartingBalanceUsed.set(accountName, actualUsed);
            totalFromAccounts += actualUsed;
          }
        }
      }
    });

    const totalAvailable = totalIncome + totalFromAccounts;

    // Helper to add node
    const addNode = (id: string, label: string, value: number, level: number, color: string, nodeType?: FlowNode["nodeType"]) => {
      const node: FlowNode = {
        id,
        label,
        value,
        x: 40 + level * levelWidth,
        y: 0,
        height: Math.max(nodeHeight, Math.min(100, value / 50)),
        color,
        level,
        nodeType,
      };
      if (!nodesByLevel.has(level)) nodesByLevel.set(level, []);
      nodesByLevel.get(level)!.push(node);
      allNodes.push(node);
      return node;
    };

    // LEVEL 0: Income only
    let totalIncomeNode: FlowNode | undefined;
    
    if (totalIncome > 0) {
      totalIncomeNode = addNode("total-income", "Income", totalIncome, 0, "hsl(210, 70%, 55%)", "total");
    }

    // LEVEL 1: Income Sources (categories) + Account Balances (both feed into Available)
    const incomeSourceNodes = new Map<string, FlowNode>();
    incomeByCategory.forEach((amount, category) => {
      const node = addNode(`income-source-${category}`, category, amount, 1, "hsl(210, 60%, 60%)", "income-source");
      incomeSourceNodes.set(category, node);
    });

    // Add account balance nodes at level 1 (same column as income sources)
    const accountBalanceNodes = new Map<string, FlowNode>();
    const sortedAccountBalances = Array.from(accountStartingBalanceUsed.entries())
      .sort((a, b) => b[1] - a[1]);
    
    sortedAccountBalances.forEach(([accountName, balanceUsed]) => {
      const node = addNode(
        `account-balance-${accountName}`, 
        `${accountName} (bal.)`, 
        balanceUsed, 
        1, 
        "hsl(35, 70%, 55%)", 
        "income-source"  // Same type so they layout together
      );
      accountBalanceNodes.set(accountName, node);
    });

    // Links: Total Income → Income Sources
    if (totalIncomeNode) {
      incomeSourceNodes.forEach((sourceNode, category) => {
        const amount = incomeByCategory.get(category) || 0;
        flowLinks.push({ source: totalIncomeNode!, target: sourceNode, value: amount, color: "hsl(210, 60%, 60%)" });
      });
    }

    // LEVEL 2: Available Funds (combines income + account balances)
    const availableNode = addNode("available", "Available", totalAvailable, 2, "hsl(145, 50%, 50%)", "account");

    // Links: Income Sources → Available
    incomeSourceNodes.forEach((sourceNode) => {
      flowLinks.push({ source: sourceNode, target: availableNode, value: sourceNode.value, color: "hsl(145, 50%, 50%)" });
    });

    // Links: Account Balance Nodes → Available
    accountBalanceNodes.forEach((balanceNode, accountName) => {
      const balanceUsed = accountStartingBalanceUsed.get(accountName) || 0;
      flowLinks.push({ source: balanceNode, target: availableNode, value: balanceUsed, color: "hsl(35, 60%, 55%)" });
    });

    // LEVEL 3: Expense Groups
    const groupNodes = new Map<string, FlowNode>();
    const sortedGroups = Array.from(groupTotals.entries()).sort((a, b) => b[1] - a[1]);
    
    sortedGroups.forEach(([group, total]) => {
      const node = addNode(`group-${group}`, group, total, 3, getGroupColor(group), "expense-group");
      groupNodes.set(group, node);
    });

    // Links: Available → Expense Groups
    groupNodes.forEach((groupNode, group) => {
      const groupTotal = groupTotals.get(group) || 0;
      flowLinks.push({ source: availableNode, target: groupNode, value: groupTotal, color: getGroupColor(group) });
    });

    // LEVEL 4: Individual Categories
    const categoryNodes = new Map<string, FlowNode>();
    expensesByGroup.forEach((categories, group) => {
      const groupColor = getGroupColor(group);
      categories.forEach((amount, category) => {
        const node = addNode(`category-${category}`, category, amount, 4, groupColor, "category");
        categoryNodes.set(category, node);
      });
    });

    // Links: Groups → Categories
    expensesByGroup.forEach((categories, group) => {
      const groupNode = groupNodes.get(group);
      if (groupNode) {
        categories.forEach((amount, category) => {
          const categoryNode = categoryNodes.get(category);
          if (categoryNode) {
            flowLinks.push({ source: groupNode, target: categoryNode, value: amount, color: getGroupColor(group) });
          }
        });
      }
    });

    // Position nodes vertically - first pass to get total heights per level
    const sortedLevels = Array.from(nodesByLevel.keys()).sort((a, b) => a - b);
    
    // Calculate max total height across all levels for global centering
    let maxTotalHeight = 0;
    sortedLevels.forEach(level => {
      const levelNodes = nodesByLevel.get(level)!;
      const totalHeight = levelNodes.reduce((sum, node) => sum + node.height, 0);
      const totalGaps = (levelNodes.length - 1) * minNodeGap;
      maxTotalHeight = Math.max(maxTotalHeight, totalHeight + totalGaps);
    });
    
    // Use a global center point for all levels
    const globalCenterY = Math.max(300, maxTotalHeight / 2 + 60);
    
    sortedLevels.forEach((level, levelIndex) => {
      const levelNodes = nodesByLevel.get(level)!;
      
      if (levelIndex === 0) {
        levelNodes.sort((a, b) => b.value - a.value);
      } else {
        // Sort by weighted average position of source nodes
        levelNodes.sort((a, b) => {
          const aSourceLinks = flowLinks.filter(l => l.target.id === a.id);
          const bSourceLinks = flowLinks.filter(l => l.target.id === b.id);
          
          const aAvgY = aSourceLinks.length > 0
            ? aSourceLinks.reduce((sum, l) => sum + (l.source.y + l.source.height / 2) * l.value, 0) / 
              aSourceLinks.reduce((sum, l) => sum + l.value, 0)
            : 0;
          
          const bAvgY = bSourceLinks.length > 0
            ? bSourceLinks.reduce((sum, l) => sum + (l.source.y + l.source.height / 2) * l.value, 0) / 
              bSourceLinks.reduce((sum, l) => sum + l.value, 0)
            : 0;
          
          return aAvgY - bAvgY;
        });
      }
      
      // Center each level's nodes around the global center
      const totalHeight = levelNodes.reduce((sum, node) => sum + node.height, 0);
      const totalGaps = (levelNodes.length - 1) * minNodeGap;
      const levelHeight = totalHeight + totalGaps;
      const startY = globalCenterY - levelHeight / 2;
      
      let currentY = startY;
      levelNodes.forEach(node => {
        node.y = currentY;
        currentY += node.height + minNodeGap;
      });
    });

    // Re-position nodes based on weighted Y of sources (for better alignment)
    // But maintain global vertical centering per level
    for (let pass = 0; pass < 2; pass++) {
      sortedLevels.slice(1).forEach(level => {
        const levelNodes = nodesByLevel.get(level)!;
        
        // First, calculate ideal positions based on source connections
        levelNodes.forEach(node => {
          const sourceLinks = flowLinks.filter(l => l.target.id === node.id);
          if (sourceLinks.length > 0) {
            const weightedY = sourceLinks.reduce((sum, l) => {
              const sourceNode = allNodes.find(n => n.id === l.source.id);
              return sum + (sourceNode ? (sourceNode.y + sourceNode.height / 2) * l.value : 0);
            }, 0);
            const totalValue = sourceLinks.reduce((sum, l) => sum + l.value, 0);
            node.y = weightedY / totalValue - node.height / 2;
          }
        });
        
        // Collision resolution
        const sorted = [...levelNodes].sort((a, b) => a.y - b.y);
        for (let i = 1; i < sorted.length; i++) {
          const prev = sorted[i - 1];
          const curr = sorted[i];
          const minY = prev.y + prev.height + minNodeGap;
          if (curr.y < minY) curr.y = minY;
        }
        
        // Re-center the level after collision resolution
        const levelMinY = Math.min(...levelNodes.map(n => n.y));
        const levelMaxY = Math.max(...levelNodes.map(n => n.y + n.height));
        const levelCenterY = (levelMinY + levelMaxY) / 2;
        const offsetToCenter = globalCenterY - levelCenterY;
        levelNodes.forEach(n => n.y += offsetToCenter);
      });
    }

    // Ensure all nodes are within visible bounds
    const minY = Math.min(...allNodes.map(n => n.y));
    if (minY < 50) {
      const offsetY = 50 - minY;
      allNodes.forEach(node => node.y += offsetY);
    }

    const minX = Math.min(...allNodes.map(n => n.x));
    if (minX < 40) {
      const offsetX = 40 - minX;
      allNodes.forEach(node => node.x += offsetX);
    }

    return { nodes: allNodes, links: flowLinks };
  }, [filteredTransactions, accounts, categoryClassifications]);

  useEffect(() => {
    setNodes(initialLayout.nodes);
    setLinks(initialLayout.links);
    setZoom(1);
  }, [initialLayout]);

  const handleResetLayout = useCallback(() => {
    setNodes(initialLayout.nodes);
    setLinks(initialLayout.links);
    setZoom(1);
  }, [initialLayout]);

  const handleMouseDown = useCallback((e: React.MouseEvent, nodeId: string) => {
    e.preventDefault();
    const svgRect = svgRef.current?.getBoundingClientRect();
    if (!svgRect) return;

    const node = nodes.find(n => n.id === nodeId);
    if (!node) return;

    setDragState({
      nodeId,
      startX: e.clientX,
      startY: e.clientY,
      offsetX: e.clientX - svgRect.left - node.x,
      offsetY: e.clientY - svgRect.top - node.y,
    });
  }, [nodes]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!dragState.nodeId) return;

    const svgRect = svgRef.current?.getBoundingClientRect();
    if (!svgRect) return;

    const newX = e.clientX - svgRect.left - dragState.offsetX;
    const newY = e.clientY - svgRect.top - dragState.offsetY;

    setNodes(prev => prev.map(node => 
      node.id === dragState.nodeId
        ? { ...node, x: Math.max(0, newX), y: Math.max(0, newY) }
        : node
    ));

    setLinks(prev => prev.map(link => ({
      ...link,
      source: nodes.find(n => n.id === link.source.id) || link.source,
      target: nodes.find(n => n.id === link.target.id) || link.target,
    })));
  }, [dragState, nodes]);

  const handleMouseUp = useCallback(() => {
    setDragState({
      nodeId: null,
      startX: 0,
      startY: 0,
      offsetX: 0,
      offsetY: 0,
    });
  }, []);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setZoom(z => Math.min(2, Math.max(0.25, z + delta)));
    }
  }, []);

  const formatCurrency = (amount: number) => {
    return formatCurrencyPrefs(amount);
  };

  // Calculate max values per level transition for relative thickness
  const maxValuesByTransition = useMemo(() => {
    const transitionMaxes = new Map<string, number>();
    links.forEach(link => {
      const sourceLevel = link.source.level;
      const targetLevel = link.target.level;
      const key = `${sourceLevel}-${targetLevel}`;
      const currentMax = transitionMaxes.get(key) || 0;
      transitionMaxes.set(key, Math.max(currentMax, link.value));
    });
    return transitionMaxes;
  }, [links]);

  const generatePath = useCallback((link: FlowLink, hOffset: number) => {
    const sourceNode = nodes.find(n => n.id === link.source.id) || link.source;
    const targetNode = nodes.find(n => n.id === link.target.id) || link.target;
    
    const nWidth = 160;
    const x1 = sourceNode.x + nWidth + hOffset;
    const x2 = targetNode.x + hOffset;
    const y1 = sourceNode.y + sourceNode.height / 2;
    const y2 = targetNode.y + targetNode.height / 2;
    
    // Get max value for this level transition
    const transitionKey = `${sourceNode.level}-${targetNode.level}`;
    const maxValue = maxValuesByTransition.get(transitionKey) || link.value;
    
    // Calculate thickness relative to other lines in the same transition
    // Scale from 2px (min) to max of node height * 0.5 or 16px
    const maxThickness = Math.min(16, Math.min(sourceNode.height, targetNode.height) * 0.5);
    const minThickness = 2;
    const ratio = maxValue > 0 ? link.value / maxValue : 0;
    const thickness = minThickness + ratio * (maxThickness - minThickness);

    const midX = (x1 + x2) / 2;

    return {
      path: `M ${x1} ${y1} C ${midX} ${y1}, ${midX} ${y2}, ${x2} ${y2}`,
      thickness,
    };
  }, [nodes, maxValuesByTransition]);

  if (transactions.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Money Flow</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-center text-muted-foreground py-8">
            Add transactions to see your money flow visualization
          </p>
        </CardContent>
      </Card>
    );
  }

  if (nodes.length === 0 || isClassifying) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Money Flow - Sankey Diagram</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center gap-2 py-8 text-muted-foreground">
            {isClassifying && <Loader2 className="h-4 w-4 animate-spin" />}
            <span>{isClassifying ? "Analyzing categories with AI..." : "Loading account hierarchy..."}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const nodeWidth = 160;
  const maxLevel = nodes.length > 0 ? Math.max(...nodes.map(n => n.level)) : 0;
  const contentWidth = (maxLevel + 1) * 200 + nodeWidth;
  const svgWidth = Math.max(900, contentWidth + 80);
  const svgHeight = Math.max(500, ...nodes.map(n => n.y + n.height)) + 60;
  
  // Calculate horizontal offset to center the diagram
  const horizontalOffset = Math.max(0, (svgWidth - contentWidth) / 2);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <GripVertical className="h-5 w-5 text-muted-foreground" />
            Money Flow - Sankey Diagram
            <span className="text-sm font-normal text-muted-foreground ml-auto">
              Drag nodes to reposition
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-6 items-center">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Filters:</span>
            </div>
            
            <Select value={timePeriod} onValueChange={setTimePeriod}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Time Period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="7">Last 7 Days</SelectItem>
                <SelectItem value="30">Last 30 Days</SelectItem>
                <SelectItem value="90">Last 90 Days</SelectItem>
                <SelectItem value="365">Last Year</SelectItem>
              </SelectContent>
            </Select>

            <Select value={selectedAccount} onValueChange={setSelectedAccount}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Account" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Accounts</SelectItem>
                {uniqueAccounts.map(account => (
                  <SelectItem key={account} value={account}>{account}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {uniqueCategories.map(category => (
                  <SelectItem key={category} value={category}>{category}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {(timePeriod !== "all" || selectedAccount !== "all" || selectedCategory !== "all") && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  setTimePeriod("all");
                  setSelectedAccount("all");
                  setSelectedCategory("all");
                }}
              >
                Clear Filters
              </Button>
            )}
            
            <div className="flex items-center gap-1 ml-auto">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => setZoom(z => Math.max(0.25, z - 0.25))}
                disabled={zoom <= 0.25}
              >
                <ZoomOut className="h-4 w-4" />
              </Button>
              <span className="text-sm text-muted-foreground w-12 text-center">
                {Math.round(zoom * 100)}%
              </span>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => setZoom(z => Math.min(2, z + 0.25))}
                disabled={zoom >= 2}
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="ml-2 gap-1"
                onClick={handleResetLayout}
              >
                <RotateCcw className="h-4 w-4" />
                Reset
              </Button>
            </div>
          </div>
        </CardContent>
        <CardContent>
          <div 
            className="overflow-auto rounded-lg border bg-muted/20 p-4 max-h-[70vh]"
            onWheel={handleWheel}
          >
            <svg 
              ref={svgRef}
              width={svgWidth * zoom} 
              height={svgHeight * zoom} 
              style={{ minHeight: svgHeight * zoom, minWidth: svgWidth * zoom }}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              <g transform={`scale(${zoom})`}>
              {/* Flow links with animation */}
              <AnimatePresence>
                <g>
                  {links.map((link, i) => {
                    const { path, thickness } = generatePath(link, horizontalOffset);
                    const strokeColor = link.color || "hsl(var(--primary))";
                    return (
                      <motion.path
                        key={`${link.source.id}-${link.target.id}-${i}`}
                        d={path}
                        stroke={strokeColor}
                        strokeWidth={thickness}
                        fill="none"
                        opacity="0.4"
                        initial={{ pathLength: 0, opacity: 0 }}
                        animate={{ pathLength: 1, opacity: 0.4 }}
                        exit={{ pathLength: 0, opacity: 0 }}
                        transition={{ 
                          duration: 0.8,
                          delay: i * 0.02,
                          ease: "easeInOut"
                        }}
                      />
                    );
                  })}
                </g>
              </AnimatePresence>

              {/* All nodes with animation */}
              <AnimatePresence>
                <g>
                  {nodes.map((node, index) => (
                    <motion.g 
                      key={node.id}
                      initial={{ opacity: 0, scale: 0.8, y: node.y - 20 }}
                      animate={{ opacity: 0.95, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{
                        duration: 0.5,
                        delay: index * 0.05,
                        ease: "easeOut"
                      }}
                      onMouseDown={(e) => handleMouseDown(e as any, node.id)}
                      className="cursor-move"
                      style={{ 
                        opacity: dragState.nodeId === node.id ? 0.8 : 0.95,
                        transformOrigin: `${node.x + 90}px ${node.y + node.height/2}px`
                      }}
                      whileHover={{ scale: 1.02, opacity: 1 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <motion.rect
                        x={node.x + horizontalOffset}
                        y={node.y}
                        width={nodeWidth}
                        height={node.height}
                        fill={node.color}
                        rx="8"
                        stroke={dragState.nodeId === node.id ? "hsl(var(--primary))" : "transparent"}
                        strokeWidth="2"
                        initial={{ height: 0 }}
                        animate={{ height: node.height }}
                        transition={{ duration: 0.4, delay: index * 0.05 }}
                      />
                      <motion.text
                        x={node.x + horizontalOffset + 10}
                        y={node.y + node.height / 2 - 8}
                        fill="white"
                        fontSize="12"
                        fontWeight="600"
                        dominantBaseline="middle"
                        pointerEvents="none"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3, delay: index * 0.05 + 0.2 }}
                      >
                        {node.label.length > 18 ? node.label.slice(0, 16) + "..." : node.label}
                      </motion.text>
                      <motion.text
                        x={node.x + horizontalOffset + 10}
                        y={node.y + node.height / 2 + 10}
                        fill="white"
                        fontSize="11"
                        fontWeight="500"
                        dominantBaseline="middle"
                        opacity="0.9"
                        pointerEvents="none"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.9 }}
                        transition={{ duration: 0.3, delay: index * 0.05 + 0.3 }}
                      >
                        {formatCurrency(node.value)}
                      </motion.text>
                      {/* Drag indicator dots */}
                      {[8, 16, 24].map((offset, i) => (
                        <motion.circle
                          key={`dot-${i}`}
                          cx={node.x + horizontalOffset + nodeWidth - 10}
                          cy={node.y + node.height / 2 - 8 + offset}
                          r="2"
                          fill="white"
                          opacity="0.4"
                          pointerEvents="none"
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ duration: 0.2, delay: index * 0.05 + 0.4 + i * 0.05 }}
                        />
                      ))}
                    </motion.g>
                  ))}
                </g>
              </AnimatePresence>

              {/* Column labels */}
              {nodes.some(n => n.nodeType === "total") && (
                <motion.text 
                  x={horizontalOffset + 40 + nodeWidth / 2} 
                  y="25" 
                  fill="hsl(var(--foreground))" 
                  fontSize="11" 
                  fontWeight="600" 
                  textAnchor="middle"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.1 }}
                >
                  Income
                </motion.text>
              )}
              {nodes.some(n => n.nodeType === "income-source") && (
                <motion.text 
                  x={horizontalOffset + 40 + 1 * 200 + nodeWidth / 2} 
                  y="25" 
                  fill="hsl(var(--foreground))" 
                  fontSize="11" 
                  fontWeight="600" 
                  textAnchor="middle"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.15 }}
                >
                  Sources
                </motion.text>
              )}
              {nodes.some(n => n.nodeType === "account") && (
                <motion.text 
                  x={horizontalOffset + 40 + 2 * 200 + nodeWidth / 2} 
                  y="25" 
                  fill="hsl(var(--foreground))" 
                  fontSize="11" 
                  fontWeight="600" 
                  textAnchor="middle"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.2 }}
                >
                  Available
                </motion.text>
              )}
              {nodes.some(n => n.nodeType === "expense-group") && (
                <motion.text 
                  x={horizontalOffset + 40 + 3 * 200 + nodeWidth / 2} 
                  y="25" 
                  fill="hsl(var(--foreground))" 
                  fontSize="11" 
                  fontWeight="600" 
                  textAnchor="middle"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.25 }}
                >
                  Groups
                </motion.text>
              )}
              {nodes.some(n => n.nodeType === "category") && (
                <motion.text 
                  x={horizontalOffset + 40 + 4 * 200 + nodeWidth / 2} 
                  y="25" 
                  fill="hsl(var(--foreground))" 
                  fontSize="11" 
                  fontWeight="600" 
                  textAnchor="middle"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.3 }}
                >
                  Categories
                </motion.text>
              )}
              </g>
            </svg>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};